import { codeExample, defineDemoElement, demoLayoutStyles, stripIndent } from './demo-shared.js';
import { css, html, LitElement } from 'lit';
import { state } from 'lit/decorators.js';
import type { DemoSection } from './demo-shared.js';
import type { WebmoduleSelect2Value } from '../../components/select2/select2.js';

const FRUIT_VALUES: WebmoduleSelect2Value[] = [
  { value: 'apple', text: 'Apple' },
  { value: 'orange', text: 'Orange' },
  { value: 'grape', text: 'Grape' },
  { value: 'kiwi', text: 'Kiwi', disabled: true },
  { value: 'banana', text: 'Banana' }
];

const TEAM_VALUES: WebmoduleSelect2Value[] = [
  { value: 'johan', text: 'Johan' },
  { value: 'kyley', text: 'Kyley' },
  { value: 'liselle', text: 'Liselle' },
  { value: 'alex', text: 'Alex' },
  { value: 'alexey', text: 'Alexey' },
  { value: 'shane', text: 'Shane' }
];

const SELECT_VALUES_SNIPPET = stripIndent(`
  import type { WebmoduleSelect2Value } from '@softtech/webmodule-components';

  const values: WebmoduleSelect2Value[] = [
    { value: 'apple', text: 'Apple' },
    { value: 'orange', text: 'Orange' },
    { value: 'kiwi', text: 'Kiwi', disabled: true }
  ];

  // Bind value as a property: it accepts a string or a string[] (for multiple).
  html\`<webmodule-select label="Fruit" .values=\${values} .value=\${'apple'}></webmodule-select>\`;`);

const SELECT_MULTIPLE_SNIPPET = stripIndent(`
  html\`<webmodule-select
    label="Assignees"
    .values=\${teamValues}
    .value=\${['johan', 'kyley', 'liselle', 'alex', 'alexey']}
    multiple
    clearable
    max-options-visible="3"
  ></webmodule-select>\`;`);

/** Tracks values created through the combobox's allow-create flow. */
class DemoComboboxCreate extends LitElement {
  static styles = [
    demoLayoutStyles,
    css`
      :host {
        display: block;
        max-width: 420px;
      }
    `
  ];

  @state() private created: string[] = [];

  render() {
    return html`
      <div class="demo-stack">
        <webmodule-combobox
          label="Assign a category"
          placeholder="Type to filter or create"
          allow-create
          clearable
          size="small"
          @webmodule-create=${(e: CustomEvent<{ value: string }>) => {
            this.created = [...this.created, e.detail.value];
          }}
        >
          <webmodule-option value="windows">Windows</webmodule-option>
          <webmodule-option value="doors">Doors</webmodule-option>
          <webmodule-option value="hardware">Hardware</webmodule-option>
        </webmodule-combobox>
        <div class="demo-row">
          ${this.created.length
            ? this.created.map(value => html`<webmodule-tag variant="success">${value}</webmodule-tag>`)
            : html`<span style="color: var(--webmodule-color-neutral-500);">
                No categories created yet - type a new name and pick "Create".
              </span>`}
        </div>
      </div>
    `;
  }
}

export function registerSelectDemos() {
  defineDemoElement('demo-combobox-create', DemoComboboxCreate);
}

export const selectSection: DemoSection = {
  id: 'select',
  title: 'Select (data-driven)',
  tags: ['webmodule-select'],
  summary:
    'The workhorse select: options come from a .values array of { value, text, disabled? } objects instead of ' +
    'slotted children, which suits data-bound screens. Note the tag/class naming quirk: <webmodule-select> is ' +
    'implemented by the WebmoduleSelect2 class, while the slotted-options variant lives at <webmodule-select2>.',
  render: () => html`
    <demo-example label="Options from data" .code=${SELECT_VALUES_SNIPPET}>
      <div slot="description">
        Options are plain data - no child elements to manage. Disabled options stay visible but unselectable.
      </div>
      <div class="demo-stack" style="max-width: 420px;">
        <webmodule-select label="Fruit" .values=${FRUIT_VALUES} .value=${'apple'} size="small"></webmodule-select>
        <webmodule-select
          label="Clearable"
          .values=${FRUIT_VALUES}
          .value=${'orange'}
          size="small"
          clearable
        ></webmodule-select>
        <webmodule-select
          label="Disabled"
          .values=${FRUIT_VALUES}
          .value=${'grape'}
          size="small"
          disabled
        ></webmodule-select>
      </div>
    </demo-example>

    <demo-example label="Multiple selection" .code=${SELECT_MULTIPLE_SNIPPET}>
      <div slot="description">
        With <code>multiple</code>, the value is a string array and selections render as removable tags;
        <code>max-options-visible</code> collapses the overflow into a "+n" tag.
      </div>
      <div class="demo-stack" style="max-width: 420px;">
        <webmodule-select
          label="Assignees"
          .values=${TEAM_VALUES}
          .value=${['johan', 'kyley', 'liselle', 'alex', 'alexey']}
          multiple
          clearable
          max-options-visible="3"
          size="small"
        ></webmodule-select>
      </div>
    </demo-example>

    <demo-example label="Sizes and styles" .code=${''}>
      <div class="demo-stack" style="max-width: 420px;">
        <webmodule-select placeholder="Small" .values=${FRUIT_VALUES} size="small"></webmodule-select>
        <webmodule-select placeholder="Medium" .values=${FRUIT_VALUES} size="medium"></webmodule-select>
        <webmodule-select placeholder="Large" .values=${FRUIT_VALUES} size="large"></webmodule-select>
        <webmodule-select placeholder="Pill" .values=${FRUIT_VALUES} size="medium" pill></webmodule-select>
        <webmodule-select placeholder="Filled" .values=${FRUIT_VALUES} size="medium" filled></webmodule-select>
      </div>
    </demo-example>
  `
};

export const select2Section: DemoSection = {
  id: 'select2',
  title: 'Select (slotted options)',
  tags: ['webmodule-select2', 'webmodule-option'],
  summary:
    'The slotted variant: options are <webmodule-option> children, so templates control option content directly ' +
    'and <webmodule-divider> can group them visually.',
  render: () => html`
    <demo-example
      label="Basic usage"
      .code=${stripIndent(`
        html\`<webmodule-select2 label="Fruit" size="small" .value=\${'option1'} clearable>
          <webmodule-option value="option1">Option 1</webmodule-option>
          <webmodule-option value="option2">Option 2</webmodule-option>
          <webmodule-divider></webmodule-divider>
          <webmodule-option value="option3">Option 3</webmodule-option>
          <webmodule-option value="option4" disabled>Option 4 (disabled)</webmodule-option>
        </webmodule-select2>\`;`)}
    >
      <div slot="description">
        Bind <code>value</code> as a property - it accepts a string, or a string array when <code>multiple</code> is
        set.
      </div>
      <div class="demo-stack" style="max-width: 420px;">
        <webmodule-select2 label="Fruit" size="small" .value=${'option1'} clearable>
          <webmodule-option value="option1">Option 1</webmodule-option>
          <webmodule-option value="option2">Option 2</webmodule-option>
          <webmodule-divider></webmodule-divider>
          <webmodule-option value="option3">Option 3</webmodule-option>
          <webmodule-option value="option4" disabled>Option 4 (disabled)</webmodule-option>
        </webmodule-select2>
      </div>
    </demo-example>
    <demo-example
      label="Multiple selection"
      .code=${stripIndent(`
        html\`<webmodule-select2 label="Select a few" .value=\${['option-1', 'option-3']} multiple clearable>
          <webmodule-option value="option-1">Option 1</webmodule-option>
          ...
        </webmodule-select2>\`;`)}
    >
      <div class="demo-stack" style="max-width: 420px;">
        <webmodule-select2 label="Select a few" .value=${['option-1', 'option-3']} multiple clearable size="small">
          <webmodule-option value="option-1">Option 1</webmodule-option>
          <webmodule-option value="option-2">Option 2</webmodule-option>
          <webmodule-option value="option-3">Option 3</webmodule-option>
          <webmodule-option value="option-4">Option 4</webmodule-option>
          <webmodule-option value="option-5">Option 5</webmodule-option>
        </webmodule-select2>
      </div>
    </demo-example>
    ${codeExample({
      label: 'Sizes and styles',
      code: `
        <div class="demo-stack" style="max-width: 420px;">
          <webmodule-select2 placeholder="Small" size="small">
            <webmodule-option value="option1">Option 1</webmodule-option>
            <webmodule-option value="option2">Option 2</webmodule-option>
          </webmodule-select2>
          <webmodule-select2 placeholder="Medium" size="medium">
            <webmodule-option value="option1">Option 1</webmodule-option>
            <webmodule-option value="option2">Option 2</webmodule-option>
          </webmodule-select2>
          <webmodule-select2 placeholder="Large pill" size="large" pill>
            <webmodule-option value="option1">Option 1</webmodule-option>
            <webmodule-option value="option2">Option 2</webmodule-option>
          </webmodule-select2>
          <webmodule-select2 placeholder="Filled" filled>
            <webmodule-option value="option1">Option 1</webmodule-option>
            <webmodule-option value="option2">Option 2</webmodule-option>
          </webmodule-select2>
        </div>`
    })}
  `
};

export const comboboxSection: DemoSection = {
  id: 'combobox',
  title: 'Combobox',
  tags: ['webmodule-combobox'],
  summary:
    'A text input fused with a listbox: typing filters the options, and with allow-create the user can mint a new ' +
    'option on the fly. Options can be slotted or supplied as data via the options property.',
  render: () => html`
    ${codeExample({
      label: 'Filtering',
      description: 'Type in the control to filter; the listbox shows empty-text when nothing matches.',
      code: `
        <div class="demo-stack" style="max-width: 420px;">
          <webmodule-combobox label="Product" placeholder="Type to filter" clearable size="small" empty-text="No matching products">
            <webmodule-option value="awning">Awning Window 600x900</webmodule-option>
            <webmodule-option value="sliding">Sliding Window 1200x1200</webmodule-option>
            <webmodule-option value="fixed">Fixed Lite 900x900</webmodule-option>
            <webmodule-option value="hinged">Hinged Door 820x2040</webmodule-option>
            <webmodule-option value="french">French Door Pair</webmodule-option>
          </webmodule-combobox>
        </div>`
    })}

    <demo-example
      label="Creating new options"
      .code=${stripIndent(`
        html\`<webmodule-combobox
          label="Assign a category"
          allow-create
          clearable
          @webmodule-create=\${(e: CustomEvent<{ value: string }>) => addCategory(e.detail.value)}
        >
          <webmodule-option value="windows">Windows</webmodule-option>
          ...
        </webmodule-combobox>\`;`)}
    >
      <div slot="description">
        With <code>allow-create</code>, typing a value that matches no option offers a "Create" row. The
        <code>webmodule-create</code> event carries the new text; fold it into your data so it survives re-renders.
      </div>
      <demo-combobox-create></demo-combobox-create>
    </demo-example>

    <demo-example
      label="Data-driven options"
      .code=${stripIndent(`
        const options: WebmoduleComboboxOption[] = [
          { value: 'apple', text: 'Apple' },
          { value: 'kiwi', text: 'Kiwi', disabled: true }
        ];

        html\`<webmodule-combobox label="Fruit" .options=\${options}></webmodule-combobox>\`;`)}
    >
      <div slot="description">
        The <code>options</code> property materializes matching <code>webmodule-option</code> children, and can be
        combined with authored (slotted) options.
      </div>
      <div class="demo-stack" style="max-width: 420px;">
        <webmodule-combobox
          label="Fruit"
          placeholder="Options built from data"
          size="small"
          .options=${FRUIT_VALUES.map(fruit => ({
            value: fruit.value,
            text: typeof fruit.text === 'string' ? fruit.text : fruit.value,
            disabled: fruit.disabled
          }))}
        ></webmodule-combobox>
      </div>
    </demo-example>

    ${codeExample({
      label: 'States',
      code: `
        <div class="demo-stack" style="max-width: 420px;">
          <webmodule-combobox label="Disabled" placeholder="Cannot open" disabled size="small">
            <webmodule-option value="one">One</webmodule-option>
          </webmodule-combobox>
          <webmodule-combobox label="Pill + filled" pill filled size="small">
            <webmodule-option value="one">One</webmodule-option>
            <webmodule-option value="two">Two</webmodule-option>
          </webmodule-combobox>
        </div>`
    })}
  `
};
